<template>
  <section class="wdp">
    <header class="wdp-head">
      <span class="wdp-title">
        {{ selection ? selection.name : 'Workflow output' }}
      </span>
      <span v-if="profile && profile.row_count !== undefined" class="wdp-meta">
        {{ formatCount(profile.row_count) }} rows · {{ profile.column_count }} columns
      </span>
      <span v-if="stale && !loading" class="wdp-stale">canvas changed</span>
      <span v-if="profile && profile.histograms_skipped" class="wdp-meta" title="The column summary used most of the time one request allows, so the distribution bars were left out. Add a filter or fewer sources to see them.">distributions skipped (large workflow)</span>
      <span v-if="exportNote" class="wdp-export-error">{{ exportNote }}</span>
      <span class="wdp-tools">
        <ExportMenu v-if="canExportRows" :exports="rowExports" size="sm" />
      <button class="wdp-refresh" :disabled="loading || !runnable" @click="load">
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="loading" />
        {{ loading ? 'Profiling…' : 'Refresh' }}
      </button>
      </span>
    </header>

    <div v-if="!runnable" class="wdp-blank">
      <span class="wdp-blank-icon"><font-awesome-icon :icon="['fas', 'table']" /></span>
      <p class="wdp-blank-title">Your data appears here</p>
      <p class="wdp-blank-text">Add a data attribute to the canvas to see a profile of every column and a sample of its rows. Click a card on the canvas to look at just that source.</p>
    </div>
    <div v-else-if="error" class="wdp-error-box">
      <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
      <span>{{ error }}</span>
    </div>

    <template v-else>
      <div class="wdp-cards">
        <LoadingIndicator v-if="loading && !profile" message="Profiling every column…" />
        <article v-for="c in (profile ? profile.columns : [])" :key="c.column" class="wdp-card" :class="{ 'is-focus': focused === c.column }" :data-column="c.column">
          <div class="wdp-card-head">
            <span class="wdp-type" :class="`is-${typeKind(c)}`">{{ typeGlyph(c) }}</span>
            <span class="wdp-col">{{ String(c.column).replace(/_/g, '_\u200b') }}</span>
          </div>
          <div class="wdp-card-stats">
            <span><strong>{{ formatCount(c.distinct) }}</strong> distinct</span>
            <span class="wdp-null" :title="`${c.null_pct || 0}% of rows are empty`">
              <span class="wdp-null-track"><span class="wdp-null-fill" :style="{ width: `${c.null_pct || 0}%` }"></span></span>
              {{ c.null_pct || 0 }}% empty
            </span>
          </div>
          <div v-if="c.values" class="wdp-ident">
            <font-awesome-icon :icon="['fas', 'user']" />
            <span>Identifier column. Values are not profiled; see the rows below.</span>
          </div>
          <div v-else-if="c.histogram" class="wdp-bars">
            <div v-for="(b, i) in c.histogram.bins" :key="i" class="wdp-bar-row" :title="`${binLabel(c, b)}: ${formatCount(b.count)}`">
              <span class="wdp-bar-label">{{ binLabel(c, b) }}</span>
              <span class="wdp-bar-track"><span class="wdp-bar" :style="{ width: barWidth(b.count, c.histogram.bins) }"></span></span>
              <span class="wdp-bar-count">{{ formatCount(b.count) }}</span>
            </div>
          </div>
          <div v-else class="wdp-bars">
            <div v-for="t in c.top_values" :key="String(t.value)" class="wdp-bar-row" :title="`${t.value}: ${formatCount(t.count)}`">
              <span class="wdp-bar-label">{{ t.value === null ? '(empty)' : t.value }}</span>
              <span class="wdp-bar-track"><span class="wdp-bar" :style="{ width: barWidth(t.count, c.top_values) }"></span></span>
              <span class="wdp-bar-count">{{ formatCount(t.count) }}</span>
            </div>
            <p v-if="!c.top_values || !c.top_values.length" class="wdp-none">No repeated values</p>
          </div>
        </article>
      </div>

      <div class="wdp-grid">
        <p v-if="rows.length" class="wdp-caption">Sample of {{ rows.length }} rows · the cards above profile every row</p>
        <table v-if="rows.length">
          <thead><tr><th v-for="col in rowColumns" :key="col">{{ col }}</th></tr></thead>
          <tbody>
            <tr v-for="(r, i) in rows" :key="i">
              <td v-for="col in rowColumns" :key="col">{{ r[col] }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else-if="!loading" class="wdp-empty">{{ loadedStamp ? 'No rows.' : 'Press Refresh to load the columns and rows.' }}</p>
      </div>
    </template>
  </section>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import abortable from '@/mixins/abortable';
import { workflowConfigKey } from '@/utils/workflowConfigKey';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import { downloadBlob, fileSlug } from '@/utils/workflowExports';

const API_BASE = '/api/etl_module/data-workflows/';
const ROW_LIMIT = 100;

export default {
  name: 'WorkflowDataPane',
  components: { LoadingIndicator, ExportMenu },
  mixins: [abortable],
  props: {
    studioConfig: { type: Object, default: null },
    selection: { type: Object, default: null },
    workflowKey: { type: [Number, String], default: null },
    exportWorkflow: { type: Object, default: null },
  },
  data() {
    return { profile: null, rows: [], rowColumns: [], loading: false, error: '', loadedStamp: '', focused: '', exporting: '', exportNote: '' };
  },
  computed: {
    target() {
      if (this.selection) {
        return { steps: [{ workflow_id: this.selection.workflow_id, role: 'population', join_on_sql: '' }] };
      }
      return this.studioConfig;
    },
    runnable() {
      return !!(this.target && this.target.steps && this.target.steps.length);
    },
    stamp() {
      return workflowConfigKey(this.target);
    },
    canExportRows() {
      return !!this.exportWorkflow && !this.selection && canAccessFeature(FEATURES.ACTION_EXPORT_WORKFLOW_ROWS);
    },
    rowExports() {
      return [
        { type: 'csv', menuLabel: 'All rows · CSV (up to 100,000)', loading: this.exporting === 'csv', action: () => this.exportRows('csv') },
        { type: 'excel', menuLabel: 'All rows · Excel (up to 100,000)', loading: this.exporting === 'xlsx', action: () => this.exportRows('xlsx') },
      ];
    },
    stale() {
      return !!this.loadedStamp && this.loadedStamp !== this.stamp;
    },
  },
  watch: {
    selection() {
      this.load();
    },
    workflowKey() {
      this.loadedStamp = '';
      this.load();
    },
    stamp() {
      if (!this.loadedStamp && !this.loading) this.load();
    },
  },
  mounted() {
    if (this.runnable) this.load();
  },
  methods: {
    async exportRows(file) {
      const wf = this.exportWorkflow;
      this.exporting = file;
      this.exportNote = '';
      try {
        const { data } = await axios.get(`${API_BASE}${wf.id}/export-rows/`, { params: { file }, responseType: 'blob' });
        downloadBlob(data, `${fileSlug(wf.name)}.${file}`);
      } catch (error) {
        const text = error.response && error.response.data instanceof Blob ? await error.response.data.text() : '';
        this.exportNote = (text && JSON.parse(text).error) || 'Could not export the rows.';
      } finally {
        this.exporting = '';
      }
    },
    async load() {
      if (!this.runnable) return;
      const signal = this._renewAbortSignal('data');
      const body = { studio_config: this.target };
      const stamp = this.stamp;
      this.loading = true;
      this.error = '';
      try {
        const [prof, sample] = await Promise.all([
          axios.post(`${API_BASE}profile/`, body, { signal }),
          axios.post(`${API_BASE}preview-sample/`, { ...body, limit: ROW_LIMIT }, { signal }),
        ]);
        if (prof.data.error || sample.data.error) {
          this.error = prof.data.error || sample.data.error;
          this.profile = null;
          this.rows = [];
        } else {
          this.profile = prof.data;
          this.rows = sample.data.rows || [];
          this.rowColumns = sample.data.columns || [];
        }
        this.loadedStamp = stamp;
      } catch (error) {
        if (this._isAborted(error)) return;
        this.error = error.response?.data?.error || 'Could not load the data.';
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },
    focusColumn(name) {
      this.focused = name;
      const card = this.$el.querySelector(`[data-column="${CSS.escape(name)}"]`);
      if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    },
    formatCount(n) {
      return typeof n === 'number' ? n.toLocaleString() : (n ?? '');
    },
    typeKind(c) {
      if (c.values) return 'identifier';
      if (c.histogram) return c.histogram.kind;
      return 'text';
    },
    typeGlyph(c) {
      return { identifier: 'ID', date: '◷', number: '#', text: 'Abc' }[this.typeKind(c)];
    },
    binLabel(c, b) {
      if (c.histogram.kind === 'date') return b.from;
      const fmt = (v) => (Math.abs(v) >= 100 ? Math.round(v).toLocaleString() : Number(v.toFixed(2)).toString());
      return `${fmt(b.from)}–${fmt(b.to)}`;
    },
    barWidth(count, list) {
      const max = Math.max(1, ...list.map((x) => x.count || 0));
      return `${Math.max(2, Math.round((100 * (count || 0)) / max))}%`;
    },
  },
};
</script>

<style scoped>
.wdp { display: flex; flex-direction: column; min-height: 0; height: 100%; background: #FFFFFF; }
.wdp-head { display: flex; align-items: center; gap: 8px; padding: 8px 16px; border-bottom: 1px solid #E8E9E9; }
.wdp-title { font-size: 13px; font-weight: 600; color: #1E1F21; }
.wdp-meta { font-size: 12px; color: #626264; }
.wdp-stale { font-size: 11px; font-weight: 600; color: #9F4D30; background: #FFF7F4; border: 1px solid #FDC6B3; border-radius: 9999px; padding: 0 8px; }
.wdp-export-error { font-size: 12px; color: #D53D3D; }
.wdp-tools { margin-left: auto; display: inline-flex; align-items: center; gap: 8px; }
.wdp-refresh { display: inline-flex; align-items: center; gap: 8px; border: 1px solid #4C6E97; background: #FFFFFF; border-radius: 4px; padding: 4px 8px; font-size: 12px; color: #4C6E97; cursor: pointer; }
.wdp-refresh:hover:not(:disabled) { box-shadow: 0 0 40px rgba(0,0,0,.10); }
.wdp-refresh:disabled { border-color: #E8E9E9; color: #8E8F90; cursor: default; }
.wdp-refresh:focus-visible { outline: 2px solid #4573D2; outline-offset: 4px; }
.wdp-empty { margin: 0; padding: 16px; font-size: 12px; color: #626264; }
.wdp-blank { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 20px; text-align: center; }
.wdp-blank-icon { width: 40px; height: 40px; border-radius: 8px; background: #EBF4FF; color: #385883; display: flex; align-items: center; justify-content: center; font-size: 16px; }
.wdp-blank-title { margin: 0; font-size: 14px; font-weight: 600; color: #1E1F21; }
.wdp-blank-text { margin: 0; max-width: 460px; font-size: 12px; line-height: 1.5; color: #626264; }
.wdp-error-box { margin: 16px; padding: 8px 16px; display: flex; align-items: center; gap: 8px; border: 1px solid #F28181; background: #FDECEC; border-radius: 8px; font-size: 12px; color: #D53D3D; }
.wdp-cards { display: flex; gap: 8px; padding: 16px; overflow-x: auto; flex-shrink: 0; border-bottom: 1px solid #E8E9E9; background: #FAFAFA; }
.wdp-card { flex: 0 0 220px; height: 200px; display: flex; flex-direction: column; gap: 8px; background: #FFFFFF; border: 1px solid #E8E9E9; border-radius: 8px; padding: 8px; }
.wdp-card.is-focus { border-color: #4573D2; box-shadow: 0 0 40px rgba(0,0,0,.10); }
.wdp-card-head { display: flex; align-items: flex-start; gap: 8px; }
.wdp-type { flex-shrink: 0; min-width: 28px; padding: 1px 4px; border-radius: 4px; font-size: 10px; font-weight: 700; text-align: center; background: #F4F4F4; color: #4B4C4D; }
.wdp-type.is-number { background: #EBF4FF; color: #385883; }
.wdp-type.is-identifier { background: #F4F4F4; color: #626264; }
.wdp-col { flex: 1; min-width: 0; font: 600 12px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace; color: #1E1F21; overflow-wrap: break-word; }
.wdp-card-stats { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 11px; color: #626264; }
.wdp-card-stats strong { color: #353537; font-weight: 600; }
.wdp-null { display: inline-flex; align-items: center; gap: 4px; }
.wdp-null-track { width: 36px; height: 4px; border-radius: 9999px; background: #E8E9E9; overflow: hidden; }
.wdp-null-fill { display: block; height: 100%; background: #8E8F90; }
.wdp-ident { display: flex; gap: 8px; align-items: flex-start; font-size: 11px; line-height: 1.45; color: #626264; }
.wdp-bars { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 4px; }
.wdp-bar-row { display: flex; align-items: center; gap: 8px; }
.wdp-bar-label { flex: 0 0 72px; font-size: 11px; color: #353537; overflow-wrap: anywhere; line-height: 1.2; }
.wdp-bar-track { flex: 1; height: 8px; border-radius: 4px; background: #F4F4F4; overflow: hidden; }
.wdp-bar { display: block; height: 100%; border-radius: 4px; background: #7799C2; }
.wdp-bar-count { flex: 0 0 auto; min-width: 28px; text-align: right; font-size: 11px; color: #626264; font-variant-numeric: tabular-nums; }
.wdp-none { margin: 0; font-size: 11px; color: #626264; }
.wdp-grid { flex: 1; min-height: 0; overflow: auto; }
.wdp-caption { margin: 0; padding: 8px 16px; font-size: 11px; color: #626264; }
.wdp-grid table { border-collapse: collapse; font-size: 12px; min-width: 100%; }
.wdp-grid th { position: sticky; top: 0; background: #F4F4F4; text-align: left; font-weight: 600; color: #1E1F21; padding: 8px 16px; border-bottom: 1px solid #E8E9E9; white-space: nowrap; }
.wdp-grid td { padding: 4px 16px; border-bottom: 1px solid #E8E9E9; color: #353537; white-space: nowrap; }
.wdp-grid tbody tr:hover td { background: #FAFAFA; }
</style>
